import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import DialogActions from "@mui/material/DialogActions";
import ModalBasicLayout from "../../../../components/Modal/ModalBasicLayout";
import ContainerModalActions from "../../../../components/Containers/ContainerModalActions";
import Loader from "../../../../components/Loaders/Loader";
import { useTranslationApp } from "../../../../lib/i18next";
import {
  BRAND_INSIGHTS_APPLY_TITLE,
  BRAND_INSIGHTS_APPLY_LOADING,
  BRAND_INSIGHTS_APPLY_CONFIRM,
  BRAND_INSIGHTS_APPLY_CANCEL,
  BRAND_INSIGHTS_APPLY_FIELD,
  BRAND_INSIGHTS_APPLY_NO_CHANGES,
  BUTTON_RETRY,
  SECTION_VOICE_TONE_DESCRIPTION,
  SECTION_OFFER_VISUAL_STYLE_LABEL,
} from "../../../../i18n/keysTranslations";
import { diffWords, hasChanges } from "../../../../utils/textDiff";
import {
  getApplyErrorMessageKey,
  isInformationalApplyError,
  isRetryableApplyError,
} from "../utils/applyErrorCopy";
import ProposalDiff from "./ProposalDiff";

/** Same labels Brand Setup uses, so the user recognises where this lands. */
const FIELD_LABELS = {
  businessTextStyle: SECTION_VOICE_TONE_DESCRIPTION,
  businessLookAndFeel: SECTION_OFFER_VISUAL_STYLE_LABEL,
};

const ApplyInsightModal = ({
  modalOpen,
  isProposing,
  isConfirming,
  proposal,
  applyError,
  onCloseModal,
  onConfirm,
  onRetry,
}) => {
  const { t } = useTranslationApp();

  const canConfirm = Boolean(proposal && !applyError);
  const canRetry = Boolean(applyError && isRetryableApplyError(applyError));
  const changed = proposal
    ? hasChanges(diffWords(proposal.currentValue, proposal.proposedValue))
    : false;

  const handleClose = () => {
    if (isConfirming) {
      return;
    }
    onCloseModal();
  };

  return (
    <ModalBasicLayout
      modalOpen={modalOpen}
      onCloseModal={handleClose}
      title={t(BRAND_INSIGHTS_APPLY_TITLE)}
      maxWidth="sm"
      fullWidth
      enableHistoryManagement={false}
    >
      {isProposing ? (
        <Loader
          hasMessage
          hasMarginTop={false}
          message={t(BRAND_INSIGHTS_APPLY_LOADING)}
        />
      ) : applyError ? (
        <Typography
          variant="body2"
          color={
            isInformationalApplyError(applyError) ? "text.secondary" : "error"
          }
        >
          {t(getApplyErrorMessageKey(applyError))}
        </Typography>
      ) : proposal ? (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {t(BRAND_INSIGHTS_APPLY_FIELD)}
            </Typography>
            <Typography variant="subtitle2">
              {FIELD_LABELS[proposal.field]
                ? t(FIELD_LABELS[proposal.field])
                : proposal.field}
            </Typography>
          </Box>

          {changed ? (
            <ProposalDiff
              currentValue={proposal.currentValue}
              proposedValue={proposal.proposedValue}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {t(BRAND_INSIGHTS_APPLY_NO_CHANGES)}
            </Typography>
          )}
        </Box>
      ) : null}

      <DialogActions sx={{ px: 0 }}>
        <ContainerModalActions
          hasPadding={true}
          propsSecondaryButton={{
            showButton: true,
            text: BRAND_INSIGHTS_APPLY_CANCEL,
            onClick: handleClose,
            disabled: isConfirming,
          }}
          propsPrimaryButton={{
            showButton: canConfirm || canRetry,
            text: canRetry ? BUTTON_RETRY : BRAND_INSIGHTS_APPLY_CONFIRM,
            onClick: canRetry ? onRetry : onConfirm,
            loading: canRetry ? isProposing : isConfirming,
            disabled: canRetry ? isProposing : !changed || isConfirming,
          }}
        />
      </DialogActions>
    </ModalBasicLayout>
  );
};

export default ApplyInsightModal;
